import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Phone, Mail, Users, MapPin, Calendar, CheckCircle2, Circle,
  Plus, Trash2, ClipboardList, Building2, List, CalendarDays
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

const ACTIVITY_TYPES = [
  { value: "call", label: "Call", icon: Phone, color: "text-blue-400", dot: "#3b82f6" },
  { value: "email", label: "Email", icon: Mail, color: "text-indigo-400", dot: "#6366f1" },
  { value: "meeting", label: "Meeting", icon: Users, color: "text-purple-400", dot: "#a855f7" },
  { value: "visit", label: "Site Visit", icon: MapPin, color: "text-amber-400", dot: "#f59e0b" },
  { value: "follow_up", label: "Follow-up", icon: Calendar, color: "text-green-400", dot: "#22c55e" },
  { value: "other", label: "Other", icon: ClipboardList, color: "text-muted-foreground", dot: "#6b7280" },
];

const TYPE_DOT_COLOR = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.value, t.dot]));

const statusConfig = {
  pending: { label: "Pending", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  completed: { label: "Completed", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  overdue: { label: "Overdue", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

function getActivityStatus(activity) {
  if (activity.completed) return "completed";
  if (!activity.due_date) return "pending";
  return new Date(activity.due_date) < new Date() ? "overdue" : "pending";
}

function toDateKey(date) {
  // YYYY-MM-DD in local time
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const EMPTY_FORM = {
  lead_id: "", activity_type: "call", title: "",
  notes: "", outcome: "", due_date: "", completed: false
};

// ── Skeleton row ──
function ActivitySkeleton() {
  return (
    <div className="flex items-start gap-3 p-3 md:p-4 rounded-sm border border-white/5 bg-card">
      <Skeleton className="w-5 h-5 rounded-full mt-0.5 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <Skeleton className="h-4 w-16 rounded-sm" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}

// ── Activity row (shared by list + calendar panel) ──
function ActivityRow({ activity, onToggle, onDelete }) {
  const typeInfo = ACTIVITY_TYPES.find(t => t.value === activity.activity_type) || ACTIVITY_TYPES[5];
  const status = getActivityStatus(activity);
  const Icon = typeInfo.icon;

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 md:p-4 rounded-sm border transition-colors",
        activity.completed
          ? "border-white/5 bg-card opacity-60"
          : "border-white/5 bg-card hover:bg-white/[0.02]"
      )}
    >
      <button
        onClick={() => onToggle(activity)}
        className="mt-0.5 flex-shrink-0"
        data-testid={`toggle-activity-${activity.id}`}
      >
        {activity.completed
          ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          : <Circle className={cn("w-5 h-5", status === "overdue" ? "text-red-400" : "text-muted-foreground/40")} />
        }
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
          <div className="flex items-center gap-1.5">
            <Icon className={cn("w-3.5 h-3.5", typeInfo.color)} strokeWidth={1.5} />
            <span className={cn("text-xs uppercase tracking-wider", typeInfo.color)}>{typeInfo.label}</span>
          </div>
          <span className="hidden sm:block text-muted-foreground/30">·</span>
          <span className={cn("text-sm font-medium", activity.completed ? "line-through text-muted-foreground" : "text-foreground")}>
            {activity.title}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Building2 className="w-3 h-3 flex-shrink-0" />
          <span>{activity.company_name}</span>
        </div>
        {activity.notes && (
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{activity.notes}</p>
        )}
        {activity.outcome && (
          <p className="text-xs text-emerald-400/70 mt-1">Outcome: {activity.outcome}</p>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <Badge variant="outline" className={cn("text-[10px] rounded-sm border", statusConfig[status]?.cls)}>
          {statusConfig[status]?.label}
        </Badge>
        {activity.due_date && (
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Calendar className="w-3 h-3" />
            {new Date(activity.due_date).toLocaleDateString("en-GB")}
          </div>
        )}
        <button
          onClick={() => onDelete(activity.id)}
          className="text-muted-foreground/30 hover:text-red-400 transition-colors"
          data-testid={`delete-activity-${activity.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("list");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const fetchActivities = useCallback(async () => {
    try {
      const res = await api.get("/activities");
      setActivities(res.data);
    } catch {
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchActivities();
    fetchLeads();
  }, [fetchActivities, fetchLeads]);

  const handleToggleComplete = async (activity) => {
    try {
      await api.put(`/activities/${activity.id}`, { completed: !activity.completed });
      toast.success(activity.completed ? "Marked as pending" : "Marked as complete");
      fetchActivities();
    } catch {
      toast.error("Failed to update activity");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/activities/${id}`);
      toast.success("Activity deleted");
      fetchActivities();
    } catch {
      toast.error("Failed to delete activity");
    }
  };

  const handleSubmit = async () => {
    if (!form.lead_id || !form.title.trim()) {
      toast.error("Lead and title are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/activities", { ...form, due_date: form.due_date || null });
      toast.success("Activity logged");
      setShowForm(false);
      setForm(EMPTY_FORM);
      fetchActivities();
    } catch {
      toast.error("Failed to log activity");
    } finally {
      setSaving(false);
    }
  };

  const filtered = activities.filter(a => {
    if (typeFilter !== "all" && a.activity_type !== typeFilter) return false;
    if (statusFilter !== "all" && getActivityStatus(a) !== statusFilter) return false;
    return true;
  });

  const counts = {
    pending: activities.filter(a => getActivityStatus(a) === "pending").length,
    overdue: activities.filter(a => getActivityStatus(a) === "overdue").length,
    completed: activities.filter(a => getActivityStatus(a) === "completed").length,
  };

  // Calendar: map date key -> activities[]
  const activitiesByDate = useMemo(() => {
    const map = {};
    activities.forEach(a => {
      if (!a.due_date) return;
      const key = toDateKey(a.due_date);
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [activities]);

  // Days that have at least one activity (for DayPicker modifier)
  const daysWithActivities = useMemo(() => {
    return Object.keys(activitiesByDate).map(k => new Date(k + 'T00:00:00'));
  }, [activitiesByDate]);

  const selectedDateKey = selectedDate ? toDateKey(selectedDate) : null;
  const selectedDateActivities = selectedDateKey ? (activitiesByDate[selectedDateKey] || []) : [];

  return (
    <div data-testid="activities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
        <div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Activities
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">Track calls, emails, visits and follow-ups</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div
            className="flex items-center border border-white/10 rounded-sm overflow-hidden"
            data-testid="view-toggle"
          >
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider transition-colors",
                view === "list"
                  ? "bg-blue-500/10 text-blue-400"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              )}
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              data-testid="view-calendar"
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider transition-colors border-l border-white/10",
                view === "calendar"
                  ? "bg-blue-500/10 text-blue-400"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              )}
            >
              <CalendarDays className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>

          <Button
            onClick={() => setShowForm(true)}
            className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-5"
            data-testid="add-activity-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Log Activity
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
        {[
          { label: "Pending", value: counts.pending, color: "text-blue-400" },
          { label: "Overdue", value: counts.overdue, color: "text-red-400" },
          { label: "Completed", value: counts.completed, color: "text-emerald-400" },
        ].map(s => (
          <Card key={s.label} className="border-white/5 bg-card rounded-sm">
            <CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</p>
              <p className={cn("text-2xl font-bold mt-1", s.color)} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {loading ? <Skeleton className="h-7 w-8 inline-block" /> : s.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Loading skeletons ── */}
      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <ActivitySkeleton key={i} />
          ))}
        </div>
      )}

      {/* ── Content (after load) ── */}
      {!loading && view === "list" && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="flex gap-1.5 flex-wrap">
              {[{ value: "all", label: "All Types" }, ...ACTIVITY_TYPES.map(t => ({ value: t.value, label: t.label }))].map(t => (
                <button
                  key={t.value}
                  onClick={() => setTypeFilter(t.value)}
                  data-testid={`filter-type-${t.value}`}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-sm border uppercase tracking-wider transition-colors",
                    typeFilter === t.value
                      ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                      : "text-muted-foreground border-white/5 hover:bg-white/5 hover:text-foreground"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { value: "all", label: "All Status" },
                { value: "pending", label: "Pending" },
                { value: "overdue", label: "Overdue" },
                { value: "completed", label: "Completed" },
              ].map(s => (
                <button
                  key={s.value}
                  onClick={() => setStatusFilter(s.value)}
                  data-testid={`filter-status-${s.value}`}
                  className={cn(
                    "px-3 py-1.5 text-xs rounded-sm border uppercase tracking-wider transition-colors",
                    statusFilter === s.value
                      ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                      : "text-muted-foreground border-white/5 hover:bg-white/5 hover:text-foreground"
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Activity List */}
          <div className="space-y-2">
            {filtered.length === 0 && activities.length === 0 ? (
              /* Empty state — no activities at all */
              <Card className="border-white/5 bg-card rounded-sm">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <ClipboardList className="w-12 h-12 text-muted-foreground/20 mb-4" strokeWidth={1} />
                  <p className="text-base font-semibold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    No activities yet
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 mb-5">
                    Log calls, emails, and meetings to track engagement
                  </p>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-9 px-5"
                    data-testid="empty-add-activity-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Activity
                  </Button>
                </CardContent>
              </Card>
            ) : filtered.length === 0 ? (
              /* Filtered — no results */
              <Card className="border-white/5 bg-card rounded-sm">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <ClipboardList className="w-10 h-10 text-muted-foreground/30 mb-3" strokeWidth={1} />
                  <p className="text-sm text-muted-foreground">No activities match the current filters</p>
                </CardContent>
              </Card>
            ) : (
              filtered.map(activity => (
                <ActivityRow
                  key={activity.id}
                  activity={activity}
                  onToggle={handleToggleComplete}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* ── Calendar View ── */}
      {!loading && view === "calendar" && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-shrink-0">
            <Card className="border-white/5 bg-card rounded-sm p-4 inline-block">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={d => setSelectedDate(d || new Date())}
                modifiers={{ hasActivity: daysWithActivities }}
                modifiersClassNames={{ hasActivity: "rdp-day--has-activity" }}
                className="rdp-gasflow"
                components={{
                  DayContent: ({ date }) => {
                    const key = toDateKey(date);
                    const dayActivities = activitiesByDate[key] || [];
                    return (
                      <div className="flex flex-col items-center gap-0.5">
                        <span>{date.getDate()}</span>
                        {dayActivities.length > 0 && (
                          <div className="flex gap-0.5 flex-wrap justify-center">
                            {dayActivities.slice(0, 3).map((a, i) => (
                              <span
                                key={i}
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                  backgroundColor: getActivityStatus(a) === "overdue"
                                    ? "#ef4444"
                                    : TYPE_DOT_COLOR[a.activity_type] || "#6b7280"
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                }}
                data-testid="activity-calendar"
              />
            </Card>
          </div>

          {/* Day panel */}
          <div className="flex-1 min-w-0">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {selectedDate
                  ? selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                  : "Select a date"}
              </p>
              {selectedDateActivities.length > 0 && (
                <Badge variant="outline" className="text-[10px] rounded-sm border-white/10 text-muted-foreground">
                  {selectedDateActivities.length} {selectedDateActivities.length === 1 ? "activity" : "activities"}
                </Badge>
              )}
            </div>

            {selectedDateActivities.length === 0 ? (
              <Card className="border-white/5 bg-card rounded-sm">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <CalendarDays className="w-10 h-10 text-muted-foreground/20 mb-3" strokeWidth={1} />
                  <p className="text-sm text-muted-foreground">No activities on this day</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {selectedDateActivities.map(activity => (
                  <ActivityRow
                    key={activity.id}
                    activity={activity}
                    onToggle={handleToggleComplete}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Activity Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-white/10 rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Log Activity
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Lead / Company *</Label>
              <Select value={form.lead_id} onValueChange={v => setForm(f => ({ ...f, lead_id: v }))}>
                <SelectTrigger className="rounded-sm border-white/10 bg-background text-sm h-9">
                  <SelectValue placeholder="Select a lead..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {leads.map(lead => (
                    <SelectItem key={lead.id} value={lead.id} className="text-sm">{lead.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Activity Type</Label>
              <Select value={form.activity_type} onValueChange={v => setForm(f => ({ ...f, activity_type: v }))}>
                <SelectTrigger className="rounded-sm border-white/10 bg-background text-sm h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {ACTIVITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-sm">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title *</Label>
              <Input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Initial call with facilities manager"
                className="rounded-sm border-white/10 bg-background text-sm h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="What was discussed..."
                className="rounded-sm border-white/10 bg-background text-sm resize-none"
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Outcome</Label>
              <Input
                value={form.outcome}
                onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}
                placeholder="e.g. Agreed to send quote"
                className="rounded-sm border-white/10 bg-background text-sm h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Due Date / Follow-up</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="rounded-sm border-white/10 bg-background text-sm h-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="completed"
                checked={form.completed}
                onCheckedChange={v => setForm(f => ({ ...f, completed: !!v }))}
                className="rounded-sm"
              />
              <Label htmlFor="completed" className="text-xs text-muted-foreground cursor-pointer">
                Mark as completed
              </Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
                className="flex-1 rounded-sm border-white/10 text-xs uppercase tracking-wider"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 rounded-sm bg-blue-500 hover:bg-blue-600 text-white text-xs uppercase tracking-wider"
              >
                {saving ? "Saving..." : "Log Activity"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
